import React, { useEffect, useState } from 'react';
import { Card, List, Typography, Space } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ThankYou from '../../public/assets/images/BLSPreview-Checkmark.svg';
import './styles.scss';

const { Text } = Typography;

interface LocalizedString {
  ar: string;
  en: string;
}

interface BLSPreviewProps {
  question: LocalizedString;
  firstAnswer: LocalizedString;
  secondAnswer: LocalizedString;
  thirdAnswer: LocalizedString;
  fourthAnswer: LocalizedString;
  fifthAnswer?: LocalizedString;
  sixthAnswer?: LocalizedString;
  noneOfTheAbove: boolean;
  language: string;
}

const BLSPreview: React.FC<BLSPreviewProps> = ({
  question,
  firstAnswer,
  secondAnswer,
  thirdAnswer,
  fourthAnswer,
  fifthAnswer,
  sixthAnswer,
  noneOfTheAbove,
  language
}) => {
  const [surveyCompleted, setSurveyCompleted] = useState<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState<LocalizedString>(question);
  const [answers, setAnswers] = useState<string[]>([]);
  const [direction, setDirection] = useState<'rtl' | 'ltr'>(language === 'ar' ? 'rtl' : 'ltr');

  const getLocalizedText = (text: LocalizedString): string => text[language];

  const isEmpty = (text: LocalizedString) => !text.ar && !text.en;
  const shouldDisplay = !(
    isEmpty(question) &&
    isEmpty(firstAnswer) &&
    isEmpty(secondAnswer) &&
    isEmpty(thirdAnswer) &&
    isEmpty(fourthAnswer) &&
    (fifthAnswer ? isEmpty(fifthAnswer) : true) &&
    (sixthAnswer ? isEmpty(sixthAnswer) : true)
  );

  useEffect(() => {
    setCurrentQuestion(question);
    setAnswers(
      [
        getLocalizedText(firstAnswer),
        getLocalizedText(secondAnswer),
        getLocalizedText(thirdAnswer),
        getLocalizedText(fourthAnswer),
        fifthAnswer ? getLocalizedText(fifthAnswer) : '',
        sixthAnswer ? getLocalizedText(sixthAnswer) : '',
        noneOfTheAbove ? (language === 'en' ? 'None of the above' : 'ولا واحدة') : ''
      ].filter((answer) => answer !== '')
    );
    setDirection(language === 'ar' ? 'rtl' : 'ltr');
  }, [
    question,
    firstAnswer,
    secondAnswer,
    thirdAnswer,
    fourthAnswer,
    fifthAnswer,
    sixthAnswer,
    noneOfTheAbove,
    language
  ]);

  if (!shouldDisplay) return null;

  const handleAnswerClick = (): void => {
    if (isEmpty(currentQuestion)) {
      return;
    }
    setSurveyCompleted(true);
  };

  const renderSurvey = (): JSX.Element => (
    <Card
      className="BLSPreview-card"
      style={{
        direction: direction,
        border: 'none'
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space align="center" style={{ direction: direction }}>
          <Text
            strong
            style={{ color: '#000000', fontSize: '0.9rem', fontWeight: '700', lineHeight: '28px' }}
          >
            {getLocalizedText(currentQuestion)}
          </Text>
        </Space>
        <List
          dataSource={answers || []}
          style={{ color: '#000000', marginTop: 8 }}
          locale={{
            emptyText: (
              <div style={{ color: '#00000', textAlign: 'center' }}>
                <Text strong style={{ color: '#000000' }}>
                  {language === 'ar' ? 'لا يوجد جوابات' : 'No answers'}
                </Text>
              </div>
            )
          }}
          renderItem={(item: string) => (
            <List.Item
              style={{
                padding: '8px 0',
                textAlign: language === 'ar' ? 'right' : 'left',
                backgroundColor: '#ffffff',
                border: '1px solid #D0D5D9',
                borderRadius: '200px',
                marginBottom: '12px',
                cursor: 'pointer'
              }}
            >
              <span
                style={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  borderRadius: '200px',
                  paddingLeft: language === 'en' ? '12px' : '0',
                  paddingRight: language === 'en' ? '0' : '12px'
                }}
                onClick={handleAnswerClick}
              >
                <p
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: '0.8rem'
                  }}
                >
                  {item}
                </p>
              </span>
            </List.Item>
          )}
        />
      </Space>
    </Card>
  );

  const renderThankYou = (): JSX.Element => (
    <Card
      className="BLSPreview-card"
      style={{
        direction: direction,
        border: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}
    >
      <img
        src={ThankYou}
        alt="thank you"
        style={{
          marginBottom: '25px',
          maxWidth: '80%',
          height: 'auto'
        }}
      />
      <p style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, lineHeight: '28px' }}>
        {language === 'ar'
          ? 'شكرًا لإجابتك، نقدّر ملاحظاتك'
          : 'Thank You For Answering, Your Feedback Is Highly Appreciated'}
      </p>
    </Card>
  );

  return (
    <div className="bls-preview-container" style={{ direction: direction }}>
      <div className="bls-preview-image">
        <div className="bls-preview-overlay" />
        <div className="bls-preview-card">
          <div className="bls-card-wrapper">
            <div className="bls-header">
              <div>
                <p>{language === 'ar' ? 'إزالة الإعلانات' : 'Remove Ads'}</p>
              </div>
              <span
                onClick={() => {
                  setSurveyCompleted(false);
                }}
              >
                <CloseCircleOutlined
                  style={{
                    fontSize: 20,
                    color: '#ffffff',
                    pointerEvents: 'none',
                    marginBottom: 10
                  }}
                />
              </span>
            </div>
            {surveyCompleted ? renderThankYou() : renderSurvey()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BLSPreview;
